import { User, UserSize } from "@/app/graphql/codegen";

/**
 * Interface für einen Benutzer mit Größeneigenschaft
 */
interface SizedUser extends User {
  size: UserSize;
}

/**
 * Interface für einen Einladungslink mit Größeneigenschaft
 */
interface InviteLink {
  id: string;
  isUsed: boolean;
  size: UserSize;
}

/**
 * Interface für Abonnementinformationen mit Sitzplatzanzahlen
 */
interface SubscriptionInfo {
  seatsS: number;
  seatsM: number;
  seatsL: number;
}

/**
 * Ergebnis der Sitzplatzbelegungsberechnung
 */
export interface SeatOccupancy {
  S: { occupied: number; total: number };
  M: { occupied: number; total: number };
  L: { occupied: number; total: number };
  total: { occupied: number; total: number };
}

/**
 * Berechnet die Sitzplatzbelegung für alle Sitzplatztypen
 * @param users - Array von Benutzern mit Größeninformationen
 * @param subscription - Abonnementinformationen mit Sitzplatzanzahlen
 * @param inviteLinks - Optionales Array von Einladungslinks (ungenutzte Einladungen zählen als belegt)
 * @returns Sitzplatzbelegungsinformationen für alle Größen und Gesamtsumme
 */
export function calculateSeatOccupancy(
  users: SizedUser[],
  subscription: SubscriptionInfo,
  inviteLinks: InviteLink[] = []
): SeatOccupancy {
  // Filter active users by size
  const usersBySize = {
    S: users.filter((user) => user.size === UserSize.S),
    M: users.filter((user) => user.size === UserSize.M),
    L: users.filter((user) => user.size === UserSize.L),
  };

  // Filter unused invite links by size
  const unusedInvitesBySize = {
    S: inviteLinks.filter((link) => !link.isUsed && link.size === UserSize.S),
    M: inviteLinks.filter((link) => !link.isUsed && link.size === UserSize.M),
    L: inviteLinks.filter((link) => !link.isUsed && link.size === UserSize.L),
  };

  // Calculate occupied seats (users + pending invites)
  const occupiedS = usersBySize.S.length + unusedInvitesBySize.S.length;
  const occupiedM = usersBySize.M.length + unusedInvitesBySize.M.length;
  const occupiedL = usersBySize.L.length + unusedInvitesBySize.L.length;

  // Get total seats from subscription
  const totalS = subscription.seatsS || 0;
  const totalM = subscription.seatsM || 0;
  const totalL = subscription.seatsL || 0;

  // Calculate totals
  const totalOccupied = occupiedS + occupiedM + occupiedL;
  const totalSeats = totalS + totalM + totalL;

  return {
    S: { occupied: occupiedS, total: totalS },
    M: { occupied: occupiedM, total: totalM },
    L: { occupied: occupiedL, total: totalL },
    total: { occupied: totalOccupied, total: totalSeats },
  };
}

/**
 * Berechnet die Sitzplatzbelegung für eine bestimmte Größe
 * @param users - Array von Benutzern mit Größeninformationen
 * @param subscription - Abonnementinformationen mit Sitzplatzanzahlen
 * @param size - Sitzplatzgröße, für die berechnet werden soll
 * @param inviteLinks - Optionales Array von Einladungslinks
 * @returns Belegte und gesamte Sitzplätze für die angegebene Größe
 */
export function calculateSeatOccupancyBySize(
  users: SizedUser[],
  subscription: SubscriptionInfo,
  size: UserSize,
  inviteLinks: InviteLink[] = []
): { occupied: number; total: number } {
  const occupancy = calculateSeatOccupancy(users, subscription, inviteLinks);
  return occupancy[size];
}

// ==========================================
// SEAT UPGRADE CALCULATIONS
// ==========================================

/**
 * Interface für Produktpreis-Informationen
 */
export interface ProductPrice {
  priceId: string;
  amount: number; // in cents
  currency: string;
  interval: string | null;
  seatType?: string | null; // S, M, L for seat prices, null for others
}

/**
 * Interface für Produktinformationen
 */
export interface Product {
  name: string;
  description: string;
  productId: string;
  prices: ProductPrice[];
}

/**
 * Interface für Abonnementinformationen mit Periodenende
 */
export interface SubscriptionWithPeriod extends SubscriptionInfo {
  currentPeriodEnd: string;
  isYearly: boolean;
  isInTrial?: boolean;
}

/**
 * Interface für Sitz-Upgrade-Kostenaufschlüsselung
 */
export interface SeatUpgradeCostBreakdown {
  seatType: 'S' | 'M' | 'L';
  quantityAdded: number;
  monthlyPrice: number; // in cents
  totalCost: number; // in cents
  remainingMonths: number;
}

/**
 * Interface für Sitz-Upgrade-Kostenberechnung
 */
export interface SeatUpgradeCostCalculation {
  remainingMonths: number;
  costBreakdown: SeatUpgradeCostBreakdown[];
  totalCost: number; // in cents
  formula: string;
}

/**
 * Extrahiert monatliche Preise aus Produktdaten
 * @param products - Array von Produkten aus der API
 * @returns Mapping von Sitztyp zu monatlichem Preis in Cent
 */
export function extractMonthlyPricesFromProducts(products: Product[]): Record<'S' | 'M' | 'L', number> {
  // Find seat prices by seatType instead of product name
  const allPrices = products.flatMap(product => product.prices);
  const sPriceData = allPrices.find(price => price.seatType === 'S');
  const mPriceData = allPrices.find(price => price.seatType === 'M');
  const lPriceData = allPrices.find(price => price.seatType === 'L');

  // Die Preise aus der API sind bereits jährliche Preise, wir müssen sie durch 12 teilen
  // und dann mit dem yearly discount (0.9) korrigieren, um den tatsächlichen monatlichen Preis zu erhalten
  const yearlyDiscount = 0.9; // 10% discount for yearly subscriptions
  
  return {
    S: Math.round((sPriceData?.amount || 0) / 12 / yearlyDiscount),
    M: Math.round((mPriceData?.amount || 0) / 12 / yearlyDiscount),
    L: Math.round((lPriceData?.amount || 0) / 12 / yearlyDiscount),
  };
}

/**
 * Berechnet die Kosten für ein Sitz-Upgrade in einem laufenden Jahresabonnement
 * Formel: (12 - currentMonthIndex) × monthly seat price × quantity added
 * 
 * @param currentSeats - Aktuelle Sitzplatzanzahlen
 * @param newSeats - Neue gewünschte Sitzplatzanzahlen
 * @param subscriptionInfo - Abonnementinformationen mit Periodenende
 * @param products - Produktdaten aus der API für Preisberechnung
 * @returns Kostenaufschlüsselung für das Upgrade
 */
export function calculateSeatUpgradeCost(
  currentSeats: { S: number; M: number; L: number },
  newSeats: { S: number; M: number; L: number },
  subscriptionInfo: SubscriptionWithPeriod,
  products: Product[]
): SeatUpgradeCostCalculation {
  if (!subscriptionInfo.isYearly) {
    throw new Error('Seat upgrade cost calculation only supported for yearly subscriptions');
  }
  
  // Extrahiere monatliche Preise aus Produktdaten
  const monthlyPrices = extractMonthlyPricesFromProducts(products);
  
  // Berechne verbleibende Monate mit derselben Logik wie Backend
  const now = new Date();
  const periodEnd = new Date(subscriptionInfo.currentPeriodEnd);
  
  // Berechne Monate bis zum Periodenende
  const monthsUntilEnd = Math.max(1, Math.ceil((periodEnd.getTime() - now.getTime()) / (30 * 24 * 60 * 60 * 1000)));
  
  // Für Jahresabonnements: verbleibende Monate = min(monthsUntilEnd, 12)
  const remainingMonths = Math.min(monthsUntilEnd, 12);
  
  const costBreakdown: SeatUpgradeCostBreakdown[] = [];
  let totalCost = 0;
  
  (['S', 'M', 'L'] as const).forEach(seatType => {
    const quantityAdded = newSeats[seatType] - currentSeats[seatType];
    if (quantityAdded > 0) {
      const monthlyPrice = monthlyPrices[seatType];
      const seatTotalCost = remainingMonths * monthlyPrice * quantityAdded;
      
      costBreakdown.push({
        seatType,
        quantityAdded,
        monthlyPrice,
        totalCost: seatTotalCost,
        remainingMonths
      });
      
      totalCost += seatTotalCost;
    }
  });
  
  // Erstelle Formel-String für Anzeige
  const formulaParts = costBreakdown.map(item => 
    `${item.remainingMonths} × €${(item.monthlyPrice / 100).toFixed(2)} × ${item.quantityAdded} ${item.seatType}`
  );
  const formula = formulaParts.length > 0 ? formulaParts.join(' + ') : 'No upgrades';
  
  return {
    remainingMonths,
    costBreakdown,
    totalCost,
    formula
  };
}

/**
 * Formatiert einen Preis in Cent zu Euro-String
 * @param priceInCents - Preis in Cent
 * @returns Formatierter Euro-String (z.B. "€19.99")
 */
export function formatPrice(priceInCents: number): string {
  return `€${(priceInCents / 100).toFixed(2)}`;
}

/**
 * Prüft ob ein Sitz-Upgrade möglich ist (mindestens ein Sitzplatz wird hinzugefügt)
 * @param currentSeats - Aktuelle Sitzplatzanzahlen
 * @param newSeats - Neue gewünschte Sitzplatzanzahlen
 * @returns true wenn ein Upgrade möglich ist
 */
export function canUpgradeSeats(
  currentSeats: { S: number; M: number; L: number },
  newSeats: { S: number; M: number; L: number }
): boolean {
  return newSeats.S > currentSeats.S || 
         newSeats.M > currentSeats.M || 
         newSeats.L > currentSeats.L;
} 